// path: src/utils/leaderboards.js
// Endless personal-best leaderboards (signed-in only).

export const LEADERBOARD_BOARD_SIZES = [4, 9, 16];

export const LEADERBOARD_CATEGORIES = [
    {
        id: "endless_level",
        label: "Highest Level",
        description: "Farthest Endless level reached on each board size.",
        valueLabel: "Level",
    },
    {
        id: "blitz_level",
        label: "Blitz Farthest",
        description: "Farthest level reached in Endless Blitz.",
        valueLabel: "Level",
    },
    {
        id: "streak",
        label: "Longest Streak",
        description: "Best perfect streak on each board size.",
        valueLabel: "Streak",
    },
    {
        id: "high_score",
        label: "High Score",
        description: "Best weighted Endless score on each board size.",
        valueLabel: "Score",
    },
];

export const LEADERBOARD_BOARD_LABELS = {
    4: "2×2",
    9: "3×3",
    16: "4×4",
};

export const LEADERBOARD_TOP_N = 50;
export const LEADERBOARD_MAX_VALUE = 9999999;

export function buildLeaderboardBoardId(categoryId, boardSize) {
    const size = Number(boardSize);
    if (!LEADERBOARD_CATEGORIES.some((item) => item.id === categoryId)) {
        return null;
    }
    if (!LEADERBOARD_BOARD_SIZES.includes(size)) {
        return null;
    }
    return `${categoryId}_${size}`;
}

export function parseLeaderboardBoardId(boardId) {
    const raw = String(boardId || "");
    const match = raw.match(
        /^(endless_level|blitz_level|streak|high_score)_(\d+)$/
    );
    if (!match) {
        return null;
    }

    const categoryId = match[1];
    const boardSize = Number(match[2]);
    if (!LEADERBOARD_BOARD_SIZES.includes(boardSize)) {
        return null;
    }

    return { boardId: raw, categoryId, boardSize };
}

export function listLeaderboardBoardIds() {
    const ids = [];
    LEADERBOARD_CATEGORIES.forEach((category) => {
        LEADERBOARD_BOARD_SIZES.forEach((size) => {
            ids.push(buildLeaderboardBoardId(category.id, size));
        });
    });
    return ids;
}

export function emptyBoardSizeMap() {
    return { 4: 0, 9: 0, 16: 0 };
}

export function normalizeBoardSizeMap(raw = null) {
    const out = emptyBoardSizeMap();
    const source = raw && typeof raw === "object" ? raw : {};

    LEADERBOARD_BOARD_SIZES.forEach((size) => {
        const value = Number(source[size] ?? source[String(size)] ?? 0);
        out[size] = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
    });

    return out;
}

export function maxBoardSizeValue(map) {
    const normalized = normalizeBoardSizeMap(map);
    return Math.max(0, ...LEADERBOARD_BOARD_SIZES.map((size) => normalized[size]));
}

/**
 * Build score payloads from endless stats for leaderboard sync/submit.
 */
export function buildLeaderboardScoresFromEndlessStats(endlessStats = null) {
    const stats = endlessStats && typeof endlessStats === "object" ? endlessStats : {};
    const levels = normalizeBoardSizeMap(stats.bestLevelByBoardSize);
    const blitz = normalizeBoardSizeMap(stats.bestBlitzLevelByBoardSize);
    const streaks = normalizeBoardSizeMap(stats.bestPerfectStreakByBoardSize);
    const highScores = normalizeBoardSizeMap(stats.bestScoreByBoardSize);

    const scores = [];

    LEADERBOARD_BOARD_SIZES.forEach((size) => {
        scores.push({
            boardId: buildLeaderboardBoardId("endless_level", size),
            value: levels[size],
        });
        scores.push({
            boardId: buildLeaderboardBoardId("blitz_level", size),
            value: blitz[size],
        });
        scores.push({
            boardId: buildLeaderboardBoardId("streak", size),
            value: streaks[size],
        });
        scores.push({
            boardId: buildLeaderboardBoardId("high_score", size),
            value: highScores[size],
        });
    });

    return scores.filter((item) => item.value > 0);
}

export function normalizeLeaderboardEntry(id, data = {}) {
    if (!id || !data || typeof data !== "object") {
        return null;
    }

    return {
        uid: String(data.uid || id),
        gid: Number(data.gid) || null,
        username: typeof data.username === "string" ? data.username : "",
        profileImageId:
            data.profileImageId !== null && data.profileImageId !== undefined
                ? data.profileImageId
                : null,
        value: Math.max(0, Math.floor(Number(data.value) || 0)),
        updatedAtMs: data.updatedAt?.toMillis
            ? data.updatedAt.toMillis()
            : Number(data.updatedAtMs) || 0,
    };
}
